import { type ApiSchemas, call } from '@rental/contracts/client'
import { format } from '@rental/i18n'
import { Button, cn, Field, Notice } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { AUTH_MODE, api, load } from '../api'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { PageHeader } from '../components/PageHeader'
import { Section } from '../components/Section'
import { Guard, LoadState } from '../components/states'
import { FIELD, ProblemNotice, StaffInput } from '../components/ui'
import { filePath } from '../files'
import { FallbackKitPdf } from '../files-ui'
import { useSend } from '../fines/send'
import { useLocale } from '../i18n'
import { problemOf } from '../problems'

type Kit = ApiSchemas['StaffFallbackKit']

export const FALLBACK_KITS_QUERY_KEY = ['fallback-kits'] as const
/** While the worker renders a kit, the list is asked again this often. */
export const KIT_POLL_MS = 2_000
const DEFAULT_SETS = 5

/**
 * S10: the printed fallback kit (Kit di emergenza). The counter asks for a
 * kit of numbered sets; the worker renders it; the screen says so and then
 * opens the PDF, to be printed while the system still answers. Nothing here
 * works when the server is down: that is what the paper is for.
 */
export function FallbackKitScreen() {
  const { messages: m } = useLocale()
  const copy = m.fallbackKit
  const { access } = useAccess()
  const allowed = can(access, 'rentals.manage')
  const queryClient = useQueryClient()
  const kits = useQuery({
    queryKey: FALLBACK_KITS_QUERY_KEY,
    queryFn: () => load(call(api.GET('/v1/staff/fallback-kits'))),
    enabled: allowed,
    // While the worker renders a kit, the screen asks until it is there.
    refetchInterval: (query) =>
      query.state.data?.items.some((kit) => kit.state === 'preparing') ? KIT_POLL_MS : false,
  })
  const [sets, setSets] = useState(String(DEFAULT_SETS))
  const [asked, setAsked] = useState<Kit | null>(null)
  const { send, busy, problem } = useSend()
  const max = kits.data?.maxSets ?? null
  const count = /^\d{1,2}$/.test(sets.trim()) ? Number(sets.trim()) : Number.NaN
  const valid = max !== null && Number.isInteger(count) && count >= 1 && count <= max

  async function prepare(event: FormEvent) {
    event.preventDefault()
    if (!valid || busy) return
    const kit = await send(() =>
      call(api.POST('/v1/staff/fallback-kits', { body: { sets: count } })),
    )
    if (!kit) return
    setAsked(kit)
    await queryClient.invalidateQueries({ queryKey: FALLBACK_KITS_QUERY_KEY })
  }

  return (
    <Guard allowed={allowed} title={copy.title}>
      <PageHeader title={copy.title} intro={copy.intro} />
      {asked ? (
        <OutcomeNotice key={asked.id}>
          <p>
            {format(copy.asked, {
              kit: asked.kit,
              sets: asked.sets,
              first: asked.first,
              last: asked.last,
            })}
          </p>
        </OutcomeNotice>
      ) : null}
      <Section title={copy.prepare}>
        <form className="flex flex-col gap-4" onSubmit={(event) => void prepare(event)}>
          <Field
            className={cn(FIELD, 'max-w-xs')}
            label={copy.sets}
            hint={max === null ? undefined : format(copy.setsHint, { max })}
            required
            error={
              sets.trim() !== '' && max !== null && !valid ? format(copy.setsHint, { max }) : null
            }
          >
            {(control) => (
              <StaffInput
                {...control}
                inputMode="numeric"
                autoComplete="off"
                value={sets}
                onChange={(event) => setSets(event.target.value)}
              />
            )}
          </Field>
          <p className="text-sm text-text-muted">{copy.reprint}</p>
          <ProblemNotice problem={problem} />
          <div>
            <Button variant="staff" type="submit" busy={busy} disabled={!valid}>
              {copy.prepare}
            </Button>
          </div>
        </form>
      </Section>
      <Section title={copy.recent}>
        <LoadState data={kits.data} problem={problemOf(kits.error)}>
          {(list) =>
            list.items.length === 0 ? (
              <p className="text-text-muted">{copy.none}</p>
            ) : (
              <ul className="flex flex-col gap-4">
                {list.items.map((kit) => (
                  <KitRow key={kit.id} kit={kit} />
                ))}
              </ul>
            )
          }
        </LoadState>
      </Section>
    </Guard>
  )
}

/** One kit: its sets, its terms, and its state in one status that says when it changes. */
function KitRow({ kit }: { kit: Kit }) {
  const { messages: m } = useLocale()
  const copy = m.fallbackKit
  const state =
    kit.state === 'preparing' ? (
      <p>{format(copy.preparing, { kit: kit.kit })}</p>
    ) : kit.state === 'ready' ? (
      <p>{format(copy.ready, { kit: kit.kit, sets: kit.sets, version: kit.termsVersion })}</p>
    ) : (
      <Notice tone="warning">
        <p>{format(copy.failed, { kit: kit.kit })}</p>
      </Notice>
    )
  return (
    <li className="flex flex-col gap-2">
      <p className="text-sm text-text-muted">
        {format(copy.range, { first: kit.first, last: kit.last })}
      </p>
      <div role="status" className="flex flex-col">
        {state}
      </div>
      {kit.state === 'ready' ? <KitLink kit={kit} /> : null}
    </li>
  )
}

function KitLink({ kit }: { kit: Kit }) {
  const { messages: m } = useLocale()
  if (AUTH_MODE === 'token') return <FallbackKitPdf kitId={kit.id} kit={kit.kit} />
  return (
    <a
      href={filePath({ kind: 'fallbackKit', kitId: kit.id })}
      target="_blank"
      rel="noopener"
      className="flex min-h-touch items-center gap-2 self-start"
    >
      <FileText aria-hidden="true" className="size-6" />
      {format(m.fallbackKit.open, { kit: kit.kit })}
    </a>
  )
}
